import React, { useState, useEffect } from 'react'
import AdminApi from './../../../../api/admin-api/admin-api'
import ComplianceDisclaimer from './../../../../components/shared/ComplianceDisclaimer/ComplianceDisclaimer.component'
import AgentCard from './AgentCard.component'

import './AgentSearch.component.scss'

// Single un-paginated page of results. Each search draws a fresh random seed,
// so re-submitting (or refreshing) the page reshuffles which agents are shown.
const PER_PAGE = 10

const AgentSearch = () => {
  const [query, setQuery] = useState('')
  const [submittedQuery, setSubmittedQuery] = useState('')
  const [pagination, setPagination] = useState({
    page: 0,
    per_page: PER_PAGE,
    total: 0,
  })
  const [results, setResults] = useState([])
  const [fallback, setFallback] = useState({ applied: false, state: null })
  const [searchType, setSearchType] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [hasSearched, setHasSearched] = useState(false)

  const runSearch = async (queryVal, seedVal) => {
    setLoading(true)
    setError('')
    setFallback({ applied: false, state: null })
    setSearchType('')
    try {
      const res = await AdminApi.searchPublicAgents({
        query: queryVal,
        seed: seedVal,
        page: 0,
        per_page: PER_PAGE,
      })
      const payload = res && res.data ? res.data : {}
      if (!payload.apistatus) {
        throw new Error(payload.msg || 'Search failed.')
      }
      setResults(Array.isArray(payload.data) ? payload.data : [])
      setPagination(payload.pagination || { page: 0, per_page: PER_PAGE, total: 0 })
      setFallback(payload.fallback || { applied: false, state: null })
      setSearchType(payload.searchType || '')
    } catch (e) {
      setResults([])
      setPagination({ page: 0, per_page: PER_PAGE, total: 0 })
      setFallback({ applied: false, state: null })
      setSearchType('')
      setError('Something went wrong searching for agents. Please try again.')
    } finally {
      setLoading(false)
      setHasSearched(true)
    }
  }

  // Validate, sync the ?q= query string (so the page is refreshable /
  // shareable), then run the search.
  const submitSearch = (rawVal, { syncUrl = true } = {}) => {
    const cleaned = `${rawVal}`.trim()
    if (!cleaned) {
      setError('Please enter a city, state, ZIP code, or agent name.')
      return
    }
    const newSeed = Math.floor(Math.random() * 2147483647)
    setQuery(cleaned)
    setSubmittedQuery(cleaned)
    if (syncUrl && typeof window !== 'undefined' && window.history) {
      window.history.replaceState({}, '', `${window.location.pathname}?q=${encodeURIComponent(cleaned)}`)
    }
    runSearch(cleaned, newSeed)
  }

  const handleSubmit = e => {
    e.preventDefault()
    submitSearch(query)
  }

  // On load, run the search automatically when the URL carries a ?q= or
  // legacy ?zip= param.
  useEffect(() => {
    if (typeof window === 'undefined') return
    const params = new URLSearchParams(window.location.search)
    const q = params.get('q') || params.get('zip')
    if (q && `${q}`.trim()) submitSearch(`${q}`.trim(), { syncUrl: false })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const total = pagination && pagination.total ? pagination.total : 0
  const isNational = !!(fallback && fallback.applied && fallback.scope === 'national')

  return (
    <section className='agent-search'>
      <div className='agent-search__inner'>
        <header className='agent-search__head'>
          <h1 className='agent-search__heading'>Find a Licensed Agent</h1>
          <p className='agent-search__subheading'>
            Search by city, state, ZIP code, or agent name to connect with a trusted USA Benefits Group agent.
          </p>
        </header>

        <form className='agent-search__form' onSubmit={handleSubmit} noValidate>
          <input
            type='text'
            className='agent-search__input'
            placeholder='Enter City, State, Zip or Agent Name'
            aria-label='Search by city, state, zip, or agent name'
            maxLength={100}
            value={query}
            onChange={e => setQuery(e.target.value)}
          />
          <button type='submit' className='agent-search__submit' disabled={loading}>
            {loading ? 'Searching\u2026' : 'Search'}
          </button>
        </form>

        <ComplianceDisclaimer variant='short' tone='dark' />

        {error ? <p className='agent-search__error'>{error}</p> : null}

        {fallback && fallback.applied && fallback.state ? (
          <p className='agent-search__notice'>
            {fallback.zipMatches > 0 ? (
              <>
                Showing agents in {submittedQuery} first, plus licensed agents across {fallback.state}.
              </>
            ) : (
              <>
                No agents found in {submittedQuery} &mdash; showing licensed agents across {fallback.state}.
              </>
            )}
          </p>
        ) : isNational && total > 0 ? (
          <p className='agent-search__notice'>
            {searchType === 'zip' ? (
              <>No agents found near {submittedQuery} &mdash; showing licensed agents nationwide who can assist you.</>
            ) : (
              <>
                No agents found for &ldquo;{submittedQuery}&rdquo; &mdash; showing licensed agents nationwide who can
                assist you.
              </>
            )}
          </p>
        ) : null}

        {loading ? (
          <div className='agent-search__status'>Searching for agents&hellip;</div>
        ) : hasSearched && total === 0 && !error ? (
          <div className='agent-search__status'>
            {isNational ? (
              'No agents are currently available. Please check back soon.'
            ) : (
              <>No agents found for &ldquo;{submittedQuery}&rdquo;. Try a different search term.</>
            )}
          </div>
        ) : results.length ? (
          <div className='agent-search__results'>
            {results.map(agent => (
              <AgentCard key={agent.id} agent={agent} />
            ))}
          </div>
        ) : null}
      </div>
    </section>
  )
}

export default AgentSearch
